import { type UserFileRead } from '@/uppy-file-upload/schemas'
import type { FileUploadState } from '@/uppy-file-upload/store/slice'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { MedChronFileStatus } from '../components/add-files/MedChronFileCard'

export type MedChronFileCardState = {
  fileName: string
  status: Exclude<MedChronFileStatus, MedChronFileStatus.EXTRACTED>
  errorMessage: string
  ready: boolean
}

export function isActiveMedChronUserFileUpload(uploadState?: FileUploadState): uploadState is FileUploadState {
  return !!uploadState && ['validating', 'queued', 'initializing', 'uploading'].includes(uploadState.client.uploadStatus)
}

export function getMedChronUserFileCardState(params: {
  fileId?: string
  file?: UserFileRead
  uploadState?: FileUploadState
  loadError?: boolean
}): MedChronFileCardState {
  const { fileId, file, uploadState, loadError } = params
  const clientStatus = uploadState?.client.uploadStatus
  const record = uploadState?.backend?.record ?? file
  const fileName = record?.original_filename ?? uploadState?.client.filename ?? fileId ?? 'File'

  if (clientStatus === 'unsupported_type') {
    return { fileName, status: MedChronFileStatus.ERROR, errorMessage: 'Unsupported file type', ready: false }
  }

  if (clientStatus === 'error') {
    return { fileName, status: MedChronFileStatus.ERROR, errorMessage: uploadState?.client.uploadError ?? 'Upload failed', ready: false }
  }

  if (loadError && !record) {
    return { fileName, status: MedChronFileStatus.ERROR, errorMessage: 'Could not load file', ready: false }
  }

  if (isActiveMedChronUserFileUpload(uploadState)) {
    return { fileName, status: MedChronFileStatus.UPLOADING, errorMessage: '', ready: false }
  }

  if (!record) {
    return { fileName, status: MedChronFileStatus.PROCESSING, errorMessage: '', ready: false }
  }

  if (record.processing_status === UserFileProcessingStatus.Failed) {
    return { fileName, status: MedChronFileStatus.ERROR, errorMessage: record.processing_error ?? 'Processing failed', ready: false }
  }

  if (record.processing_status === UserFileProcessingStatus.Pending || record.processing_status === UserFileProcessingStatus.Processing) {
    return { fileName, status: MedChronFileStatus.PROCESSING, errorMessage: '', ready: false }
  }

  return { fileName, status: MedChronFileStatus.COMPLETED, errorMessage: '', ready: true }
}
